import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import MiniSparklineSVG from "@/components/MiniSparklineSVG";
import SeedInputPanel from "@/components/SeedInputPanel";
import UndoActionLabel from "@/components/UndoActionLabel";

/**
 * Iter 78 — ThresholdHistoryCard. Extracted from NotificationsPage
 * (~275 lines) so the host page keeps shrinking toward the
 * 700-line guideline.
 *
 * Props:
 *   history         : array of snapshots (from /admin/analytics/threshold-history)
 *   setHistory      : setter used to refresh after seed / clear
 *   proHintSeen     : boolean local flag from parent
 *   setProHintSeen  : setter used by the "Reset pro hint" flow
 *
 * All previously-shipped Iter 57-77 UX behaviours are preserved.
 */
export default function ThresholdHistoryCard({
  history,
  setHistory,
  proHintSeen,
  setProHintSeen,
}) {
  if (!(history && history.length >= 2)) return null;

  const refetch = () => {
    api.get("/admin/analytics/threshold-history?limit=20")
      .then((res) => setHistory(res.data?.history || []))
      .catch(() => {});
  };

  return (
    <div className="border border-zinc-800 rounded-sm px-3 py-3" data-testid="threshold-ab-history">
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
          Threshold A/B history · last {history.length} changes
        </div>
        <div className="flex items-center gap-4">
          <SeedInputPanel
            onSeed={async (values) => {
              try {
                const payload = values ? { values } : undefined;
                const r = await api.post("/admin/analytics/seed-threshold-history", payload);
                const inserted = r.data?.inserted ?? 0;
                const responseValues = r.data?.values || [];
                toast.success("Seeded CTR variance", {
                  description: responseValues.length
                    ? `Inserted ${inserted} synthetic snapshots (${responseValues.map((v) => `${v}%`).join(" / ")}).`
                    : `Inserted ${inserted} synthetic snapshots (20% / 45% / 60%).`,
                  duration: 3000,
                });
                refetch();
              } catch (e) {
                toast.error("Seed failed", { description: formatApiErrorDetail(e) });
              }
            }}
          />
          {history.some((h) => (h.by || "").toLowerCase() === "seeder") && (
            <button
              type="button"
              data-testid="clear-seed-snapshots-btn"
              onClick={async () => {
                try {
                  const r = await api.delete("/admin/analytics/seed-threshold-history");
                  toast.success("Cleared seeded snapshots", {
                    description: `Removed ${r.data?.deleted ?? 0} demo row${r.data?.deleted === 1 ? "" : "s"} — real threshold changes preserved.`,
                    duration: 3000,
                  });
                  refetch();
                } catch (e) {
                  toast.error("Clear failed", { description: formatApiErrorDetail(e) });
                }
              }}
              className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-red-500/40 bg-red-500/5 text-red-300 hover:bg-red-500/15 transition-colors"
              title="Delete only rows with by='seeder' — real threshold changes untouched"
            >
              Clear seed
            </button>
          )}
          {proHintSeen && (
            <button
              type="button"
              data-testid="pro-hint-reset-btn"
              onClick={() => {
                let prevCount = null;
                let prevSeen = null;
                try {
                  prevCount = localStorage.getItem("stratos.csv-cancel-count");
                  prevSeen = localStorage.getItem("stratos.csv-cancel-pro-hint-seen");
                  localStorage.removeItem("stratos.csv-cancel-count");
                  localStorage.removeItem("stratos.csv-cancel-pro-hint-seen");
                } catch {}
                setProHintSeen(false);
                toast.success("Pro hint reset", {
                  description: "Cancel the auto-pin 3 more times to see the toast again.",
                  duration: 5000,
                  action: {
                    label: <UndoActionLabel seconds={5} />,
                    onClick: () => {
                      try {
                        if (prevCount != null) localStorage.setItem("stratos.csv-cancel-count", prevCount);
                        if (prevSeen != null) localStorage.setItem("stratos.csv-cancel-pro-hint-seen", prevSeen);
                      } catch {}
                      setProHintSeen(prevSeen === "1");
                      toast("Pro hint restored", { duration: 2000 });
                    },
                  },
                });
              }}
              className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-zinc-700 bg-transparent text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-colors"
              title="Clear cancel counter + pro-hint flag for demo recordings"
            >
              Reset pro hint
            </button>
          )}
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-[2px] bg-[#00C805]" /> CTR %
          </span>
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-[2px] bg-amber-400" /> Threshold s
          </span>
        </div>
      </div>
      {(() => {
        const maxCtr = history.reduce((m, h) => Math.max(m, h.ctr_pct), 0);
        if (!maxCtr) return null;
        const best = history.find((h) => h.ctr_pct === maxCtr);
        const latest = history[history.length - 1];
        const prev = history.length >= 2 ? history[history.length - 2] : null;
        const delta = prev ? Math.round((latest.ctr_pct - prev.ctr_pct) * 10) / 10 : null;
        let trend = null;
        if (delta !== null) {
          if (delta > 0) trend = { arrow: "▲", cls: "text-[#00C805]", sign: "+" };
          else if (delta < 0) trend = { arrow: "▼", cls: "text-red-400", sign: "" };
          else trend = { arrow: "▬", cls: "text-zinc-500", sign: "" };
        }
        return (
          <div
            data-testid="threshold-ab-best-callout"
            className="mb-3 mono text-[10px] uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-500/5 rounded-sm px-2 py-1 flex items-center gap-2 flex-wrap"
          >
            <span>
              Best: <span className="tabular-nums text-amber-200">{best.threshold_seconds}s</span>
              {" → "}
              <span className="tabular-nums text-amber-200">{best.ctr_pct}% CTR</span>
              <span className="normal-case tracking-normal text-zinc-500"> by {best.by || "system"}</span>
            </span>
            {trend && (
              <span
                data-testid="best-ctr-trend-arrow"
                data-trend={delta > 0 ? "up" : delta < 0 ? "down" : "flat"}
                className={`inline-flex items-center gap-1 tabular-nums ${trend.cls}`}
                title={`Latest snapshot ${latest.ctr_pct}% CTR vs previous ${prev.ctr_pct}% CTR`}
              >
                <span aria-hidden="true">{trend.arrow}</span>
                <span>{trend.sign}{delta}% vs prev</span>
              </span>
            )}
            {history.length >= 2 && (() => {
              const recent = history.slice(-3);
              const ctrs = recent.map((h) => h.ctr_pct);
              const minCtr = Math.min(...ctrs);
              const maxCtr = Math.max(...ctrs);
              const latest = ctrs[ctrs.length - 1];
              let tone = "flat";
              let toneCls = "text-zinc-500";
              let toneNumCls = "text-zinc-300";
              if (minCtr !== maxCtr) {
                if (latest === maxCtr) {
                  tone = "up";
                  toneCls = "text-[#00C805]/70";
                  toneNumCls = "text-[#00C805]";
                } else if (latest === minCtr) {
                  tone = "down";
                  toneCls = "text-red-400/70";
                  toneNumCls = "text-red-300";
                }
              }
              return (
                <>
                  <span
                    data-testid="best-ctr-trend-sparkline"
                    className="inline-flex items-center"
                    title={`Last ${recent.length} CTR snapshots: ${ctrs.map((c) => `${c}%`).join(" → ")}`}
                  >
                    <MiniSparklineSVG
                      values={ctrs}
                      up={delta >= 0}
                      width={40}
                      height={14}
                      ariaLabel="Recent CTR trend"
                    />
                  </span>
                  <span
                    data-testid="best-ctr-sparkline-delta-label"
                    data-min={minCtr}
                    data-max={maxCtr}
                    data-tone={tone}
                    className={`mono text-[9px] normal-case tracking-normal tabular-nums ${toneCls}`}
                    title={`Range across last ${recent.length} snapshots · latest ${latest}%`}
                  >
                    min <span className={toneNumCls}>{minCtr}%</span> · max <span className={toneNumCls}>{maxCtr}%</span>
                  </span>
                </>
              );
            })()}
          </div>
        );
      })()}
      <div className="flex items-center gap-4">
        <span data-testid="threshold-history-ctr-sparkline">
          <MiniSparklineSVG
            values={history.map((h) => h.ctr_pct)}
            up={true}
            width={240}
            height={40}
            ariaLabel="CTR over threshold changes"
            showLegend={true}
            annotations={(() => {
              const maxCtr = history.reduce((m, h) => Math.max(m, h.ctr_pct), 0);
              const minCtr = history.reduce((m, h) => Math.min(m, h.ctr_pct), maxCtr);
              // Iter 79 — Chart Micro-Legend. Mark the LOWEST CTR
              // snapshot with `lowlight: true` so it pops in red — a
              // paired counter-marker to the amber `highlight`
              // (highest CTR).
              // Iter 79 — Threshold Snapshot Diff. Compute the Δ CTR
              // vs previous snapshot per row and inject it into the
              // native <title> tooltip, plus flag the row with the
              // largest absolute Δ so admins spot the biggest single
              // change without eyeballing the whole chart.
              let biggestDelta = 0;
              history.forEach((h, i) => {
                if (i === 0) return;
                const d = Math.abs(h.ctr_pct - history[i - 1].ctr_pct);
                if (d > biggestDelta) biggestDelta = d;
              });
              let bestMarked = false;
              let worstMarked = false;
              return history.map((h, i) => {
                const by = h.by || "system";
                const initial = by.charAt(0).toUpperCase();
                const when = (h.at || "").slice(5, 16).replace("T", " ");
                const isBest = !bestMarked && maxCtr > 0 && h.ctr_pct === maxCtr;
                if (isBest) bestMarked = true;
                const isWorst = !worstMarked && !isBest && minCtr !== maxCtr && h.ctr_pct === minCtr;
                if (isWorst) worstMarked = true;
                const prev = i > 0 ? history[i - 1] : null;
                const deltaRaw = prev ? Math.round((h.ctr_pct - prev.ctr_pct) * 10) / 10 : null;
                const deltaStr = deltaRaw == null
                  ? ""
                  : deltaRaw > 0
                    ? ` · Δ +${deltaRaw}%`
                    : deltaRaw < 0
                      ? ` · Δ ${deltaRaw}%`
                      : " · Δ 0%";
                const isBiggestDelta = prev && biggestDelta > 0 &&
                  Math.abs(h.ctr_pct - prev.ctr_pct) === biggestDelta;
                return {
                  title: `${by} · ${h.threshold_seconds}s · ${h.ctr_pct}% CTR · ${when}${deltaStr}${isBest ? " · BEST" : ""}${isWorst ? " · WORST" : ""}${isBiggestDelta ? " · BIGGEST Δ" : ""}`,
                  label: initial,
                  highlight: isBest,
                  lowlight: isWorst,
                  delta: deltaRaw,
                  biggestDelta: isBiggestDelta,
                };
              });
            })()}
          />
        </span>
        <span data-testid="threshold-history-threshold-sparkline">
          <MiniSparklineSVG
            values={history.map((h) => h.threshold_seconds)}
            up={false}
            width={200}
            height={30}
            ariaLabel="Threshold value over changes"
          />
        </span>
        <div className="flex-1 mono text-[9px] text-zinc-500 uppercase tracking-widest text-right tabular-nums">
          latest: {history[history.length - 1].threshold_seconds}s → {history[history.length - 1].ctr_pct}% CTR
          <div className="text-zinc-600 normal-case tracking-normal">by {history[history.length - 1].by || "system"}</div>
        </div>
      </div>
    </div>
  );
}
